import {
  Avatar,
  Box,
  Button,
  Typography,
  Divider,
  Tooltip,
  TextareaAutosize,
  TextField,
} from '@mui/material'
import { Stack } from '@mui/system'
import { useState } from 'react'
import colors from '../../constants/colors'
import timeUtils from '../../utils/timeUtils'
import { resolveFileType } from '../AttachmentsCase/DataPart'
import { IFile } from '../AttachmentsCase/types'
import CardTAG from '../CardTAG'
import FormLayout from '../forms/FormLayout'
import MaterialMetadata from '../MaterialMetadata'
import TableTAG from '../TableTAG'
import { uploadInputZones } from '../UploadDropzone/types'
import DescriptionIcon from '@mui/icons-material/Description'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import textUtils from '../../utils/textUtils'
import { useNavigate } from 'react-router-dom'
import { eventTypes, ITimelineEvent } from './types'
import reportsSelectors from '../../store/reports/reportsSelectors'
import { useSelector } from 'react-redux'
import { ICaseTypeDTO } from '../../store/reports/types'
import utils from './utils'
import LabelErrorTAG from '../LabelErrorTAG'
import { textAreaStyle } from '../../constants/styleConstants'

const getTitle = (timelineEvent: ITimelineEvent) => {
  const firstNameInitial = timelineEvent?.person?.user?.first_name[0]
  const lastNameInitial = timelineEvent?.person?.user?.last_name[0]
  const initials = `${firstNameInitial ?? ''}${lastNameInitial ?? ''}`
  return (
    <Box sx={{ display: 'flex', gap: '1.4rem' }}>
      <Box>
        <Avatar
          className='dafault-font-family'
          sx={{
            width: 50,
            height: 50,
            backgroundColor: colors.WHITE,
            color: colors.BLACK,
            border: '0.2px solid black',
          }}
          src={timelineEvent?.person?.profile_pic}
        >
          {initials ?? 'JD'}
        </Avatar>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant='h4' sx={{ textTransform: 'capitalize' }}>
          {textUtils.humanize(timelineEvent.title)}
        </Typography>
        <Typography variant='labelBold' color={colors.TAG_BLUE}>
          {timelineEvent?.person?.user.first_name} {timelineEvent?.person?.user.last_name} •
          {timeUtils.getTimeFromDate(timelineEvent.created_at)}
        </Typography>
      </Box>
    </Box>
  )
}
const boxStyle = { width: '10rem', height: '10rem' }
const iconStyle = { width: '100%', height: '100%', color: colors.TAG_PURPLE }

const renderMediaUpdate = (timelineEvent: ITimelineEvent, handleSelect: (data: IFile) => void) => {
  return (
    <CardTAG title={getTitle(timelineEvent)}>
      <FormLayout>
        {timelineEvent.files?.type === uploadInputZones.TEXT_FIELD && (
          <div className='t-field t-vbox'>
            <LabelErrorTAG label={'Description'} />
            <TextareaAutosize
              value={timelineEvent.files.description}
              readOnly
              minRows={5}
              style={textAreaStyle}
              disabled
            />
          </div>
        )}
        {timelineEvent.files?.type === uploadInputZones.LINK && (
          <div className='t-field t-vbox'>
            <LabelErrorTAG label={'Link'} />
            <TextField value={timelineEvent.files.link} disabled />
          </div>
        )}
        {timelineEvent?.files?.type !== uploadInputZones.TEXT_FIELD &&
          timelineEvent?.files?.type !== uploadInputZones.LINK && (
            <div
              className='t-font-size-xs'
              dangerouslySetInnerHTML={{
                __html: timelineEvent?.files?.description as string,
              }}
            ></div>
          )}
        {!!timelineEvent?.files?.files.length && (
          <TableTAG
            columns={[
              {
                id: 'preview',
                label: 'Preview',
                format: (_: any, row: IFile) => {
                  let url = ''
                  if (row instanceof File) {
                    url = URL.createObjectURL(row)
                  }

                  const getBoxStyle = (iconStyle: any) => <Box sx={[boxStyle]}>{iconStyle}</Box>

                  const getMediaElement = (src: string) => (
                    <img src={src} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
                  )

                  if (row?.extension === uploadInputZones.TEXT_FIELD) {
                    return getBoxStyle(<TextFieldsIcon sx={[iconStyle]} />)
                  }

                  if (
                    row?.extension?.includes('text/') ||
                    row?.extension?.includes('aplication/')
                  ) {
                    return (
                      <Box sx={{ width: '10rem', height: '10rem' }}>
                        <DescriptionIcon sx={[iconStyle]} />
                      </Box>
                    )
                  }

                  const typeToCheck = row?.extension ?? row?.extension
                  if (resolveFileType(typeToCheck) === uploadInputZones.AUDIO) {
                    return getBoxStyle(<AudioFileIcon sx={[iconStyle]} />)
                  }

                  if (resolveFileType(typeToCheck) === uploadInputZones.VIDEO) {
                    return (
                      <Box sx={[boxStyle]} id='video'>
                        <video style={{ objectFit: 'cover', width: '100%', height: '100%' }}>
                          <source src={row instanceof File ? url : row?.link}></source>
                        </video>
                      </Box>
                    )
                  }

                  if (row?.link) {
                    return getBoxStyle(getMediaElement(row instanceof File ? url : row?.link))
                  }

                  return getBoxStyle(getMediaElement(row instanceof File ? url : row?.link))
                },
              },
              { id: 'name', label: 'File Name' },
              // { id: 'extension', label: 'File Type' },
            ]}
            pagination={false}
            onRowClick={handleSelect}
            rows={timelineEvent?.files?.files}
          />
        )}
      </FormLayout>
    </CardTAG>
  )
}

export const renderPageUpdate = (
  timelineEvent: ITimelineEvent,
  navigate: any,
  selectedReportTypes: ICaseTypeDTO[] | undefined,
  cls?: string,
) => {
  const data = timelineEvent?.files?.questions.map(question => {
    return {
      section: question?.text !== '' ? question?.text : question?.label,
      update_type: timelineEvent.files?.type,
      questionId: question?.id,
    }
  })
  return (
    <CardTAG title={getTitle(timelineEvent)} className={cls ? cls : ''}>
      <TableTAG
        columns={[
          {
            id: 'update_type',
            label: 'Update Type',
            format: (
              _: any,
              row: {
                section: string
                update_type: string
              },
            ) => {
              return <Typography>{textUtils.humanize(row?.update_type)}</Typography>
            },
          },

          { id: 'section', label: 'Description' },
          {
            id: 'questionId',
            label: 'Actions',
            format: (
              _: any,
              row: {
                section: string
                update_type: string
                questionId: number
              },
            ) => {
              return (
                <Tooltip title='Navigate to question' placement='top'>
                  <span>
                  <Button
                    onClick={async () => {
                      await utils.isQuestionVisible(row?.questionId, selectedReportTypes, navigate)
                    }}
                    sx={{
                      color: colors.TAG_TEXT_GRAY,
                      maxWidth: 'fit-content',
                      minWidth: 'unset',
                      padding: '0px',
                    }}
                  >
                    View
                  </Button>
                  </span>
                </Tooltip>
              )
            },
          },
        ]}
        pagination={false}
        rows={data}
      />
    </CardTAG>
  )
}

/** Group events by date (local) so each date header appears once. Preserves order within each group. */
const groupEventsByDate = (events: ITimelineEvent[]): { dateLabel: string; events: ITimelineEvent[] }[] => {
  if (!events?.length) return []
  const groups: { dateLabel: string; events: ITimelineEvent[] }[] = []
  let currentLabel = ''
  for (const event of events) {
    const label = timeUtils.formatTimelineDate(event.created_at)
    if (label !== currentLabel) {
      groups.push({ dateLabel: label, events: [event] })
      currentLabel = label
    } else {
      groups[groups.length - 1].events.push(event)
    }
  }
  return groups
}

const eventsRender = (
  events: any,
  handleSelect: (data: IFile) => void,
  navigate: any,
  selectedReportTypes: ICaseTypeDTO[] | undefined,
) => {
  const groups = groupEventsByDate(events ?? [])
  return groups.map((group, groupIndex) => (
    <div key={`timeline-group-${groupIndex}-${group.dateLabel}`} className='t-case-timeline-date-group'>
      <span className='t-case-timeline-card-title t-color-blue t-case-timeline-date-header'>
        {group.dateLabel}
      </span>
      {group.events.map((event: ITimelineEvent, index: number) => {
        const cls =
          event?.type !== eventTypes.MATERIAL_ADDED &&
          event.files?.questions &&
          event.files?.questions.length === 0
            ? 't-d-none'
            : ''
        const clsM =
          event?.type === eventTypes.MATERIAL_ADDED &&
          event.files?.type !== uploadInputZones.TEXT_FIELD &&
          event.files?.type !== uploadInputZones.LINK &&
          !event.files?.description &&
          event.files?.files &&
          event.files.files.length === 0
            ? 't-d-none'
            : ''
        return (
          <div className={`t-case-timeline-card ${cls} ${clsM}`} key={`${event?.uuid}${index}`}>
            <span className='t-case-timeline-card-title t-color-blue t-case-timeline-time-only'>
              {timeUtils.getTimeFromDate(event?.created_at)}
            </span>
            <Box sx={{ display: 'flex', gap: '1.8rem', width: '100%' }}>
              <Divider
                className={'t-separator t-background-blue'}
                orientation='vertical'
                variant='middle'
                flexItem
                sx={{ borderRightWidth: 2 }}
              />
              <Stack sx={{ width: '100%' }} spacing={2}>
                {event?.type === eventTypes.MATERIAL_ADDED && renderMediaUpdate(event, handleSelect)}
                {event?.type !== eventTypes.MATERIAL_ADDED &&
                  renderPageUpdate(event, navigate, selectedReportTypes)}
              </Stack>
            </Box>
          </div>
        )
      })}
    </div>
  ))
}

interface ICaseTimelineDataPart {
  events?: ITimelineEvent[]
}

const CaseTimelineDataPart: React.FC<ICaseTimelineDataPart> = ({ events }) => {
  const [selectedFile, setSelectedFile] = useState<IFile>()
  const selectedReportTypes = useSelector(reportsSelectors.reportCaseTypes)
  const navigate = useNavigate()

  const handleSelect = (data: IFile) => {
    setSelectedFile(data)
  }

  return (
    <>
      {events && eventsRender(events, handleSelect, navigate, selectedReportTypes)}
      <MaterialMetadata
        opened={!!selectedFile}
        close={() => {
          setSelectedFile(undefined)
        }}
        material={selectedFile}
      />
    </>
  )
}

export default CaseTimelineDataPart
